import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { getSharedPodcast, usePlayer } from "@vojomo-core/shared";
import type { SharedPodcast } from "@vojomo-core/shared";

const TYPE_LABELS: Record<string, string> = {
  deep_dive: "Deep Dive",
  summary: "Summary",
  analysis: "Analysis",
  interview: "Interview",
  briefing: "Briefing",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const PodcastShare = () => {
  const { token } = useParams<{ token: string }>();
  const [podcast, setPodcast] = useState<SharedPodcast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { controls, state } = usePlayer();

  useEffect(() => {
    if (!token) return;
    getSharedPodcast(token)
      .then((data) => setPodcast(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-gray-50">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Link not found
        </h2>
        <p className="text-sm text-gray-400">
          This podcast share link may have been revoked or is invalid.
        </p>
      </div>
    );
  }

  const handleTogglePlay = () => {
    if (!podcast.audio_url) return;
    controls.togglePlay({ itemId: podcast.audio_url, itemType: "local" });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 flex flex-col gap-6">
        {/* Branding */}
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span className="font-semibold text-blue-600">Vojomo</span>
          <span>·</span>
          <span>AI Podcast</span>
        </div>

        {/* Title block */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center text-2xl flex-shrink-0">
            🎙
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 leading-snug mb-1">
              {podcast.title}
            </h1>
            <div className="text-xs text-gray-400 flex flex-wrap gap-1">
              <span>{TYPE_LABELS[podcast.podcast_type] || podcast.podcast_type}</span>
              <span>·</span>
              <span>by {podcast.created_by_name}</span>
              <span>·</span>
              <span>{formatDate(podcast.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Player */}
        <div className="flex flex-col items-center gap-4">
          <Button
            type="primary"
            shape="circle"
            icon={
              state.paused ? (
                <PlayCircleOutlined style={{ fontSize: 28 }} />
              ) : (
                <PauseCircleOutlined style={{ fontSize: 28 }} />
              )
            }
            onClick={handleTogglePlay}
            style={{ width: 64, height: 64 }}
          />

          {/* Progress */}
          {state.duration > 0 && (
            <div className="w-full">
              <input
                type="range"
                min={0}
                max={state.duration}
                value={state.time}
                onChange={(e) => controls.seek(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{formatTime(state.time)}</span>
                <span>{formatTime(state.duration)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-center text-gray-400">
          Shared via Vojomo · Powered by AI
        </p>
      </div>
    </div>
  );
};
